import React, { useMemo, useState, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import { Copy, Check, ArrowUp, ArrowDown, ArrowLeftRight, ArrowUpDown, Info, BarChart2 } from 'lucide-react';

const formatNumber = (num) => new Intl.NumberFormat().format(num);

const formatAverage = (num) => 
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num);

const getPriceDiffInfo = (current, average) => {
  if (!average || average === 0) return { percent: 0, color: 'text-gray-400' };
  const diffPercent = ((current - average) / average) * 100;
  const color = Math.abs(diffPercent) <= 2 
    ? 'text-gray-400' 
    : diffPercent > 0 
      ? 'text-red-500'
      : 'text-green-500';
  return { percent: diffPercent, color };
};

const ItemImage = ({ itemId, name }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <div className="item_icon" style={{ position: 'relative' }}>
      {!imageLoaded && (
        <img
          className="item_icon"
          src="/items.png"
          alt="Item placeholder"
          style={{ position: 'absolute' }}
        />
      )}
      <img 
        className="item_icon"
        style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.2s' }}
        src={`https://imageserver.eveonline.com/Type/${itemId}_64.png`}
        alt={name || 'Item icon'}
        loading="lazy"
        onLoad={handleImageLoad}
      />
    </div>
  );
};

const AppraisalResults = ({
  results = [],
  sellTotal = 0, 
  buyTotal = 0, 
  priceSplit = 0, 
  volTotal = 0, 
  appraisaDate = null, 
  marketHub = '',
  rawInput = '',
  corpId = '',
  comment = '',
}) => {
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipId, setTooltipId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter out invalid items and store them separately, then sort if needed
  const { validResults, invalidItems, sortedResults } = useMemo(() => {
    const { validResults, invalidItems } = results.reduce((acc, result) => {
      if (result.error === 'Item not found') {
        acc.invalidItems.push({
          name: result.name,
          possibleMatches: result.possible_matches || []
        });
      } else {
        acc.validResults.push(result);
      }
      return acc;
    }, { validResults: [], invalidItems: [] });

    let sortedResults = [...validResults];
    
    if (sortConfig.key) {
      sortedResults.sort((a, b) => {
        const sortDirection = sortConfig.direction === 'asc' ? 1 : -1;
        
        switch (sortConfig.key) {
          case 'singleVolume':
            return sortDirection * ((a.total_item_vol/a.quantity) - (b.total_item_vol/b.quantity));
          case 'totalVolume':
            return sortDirection * (a.total_item_vol - b.total_item_vol);
          case 'singleSell':
            return sortDirection * (a.sell_price - b.sell_price);
          case 'singleBuy':
            return sortDirection * (a.buy_price - b.buy_price);
          case 'totalSell':
            return sortDirection * (a.total_sell_price - b.total_sell_price);
          case 'totalBuy':
            return sortDirection * (a.total_buy_price - b.total_buy_price);
          default:
            return 0;
        }
      });
    }

    return { validResults, invalidItems, sortedResults };
  }, [results, sortConfig]);

  // Format the appraisal date
  const formattedDate = useMemo(() => {
    if (!appraisaDate) return '-';
    const created_at_new = new Date(appraisaDate);
    return Intl.DateTimeFormat("en-UK", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    }).format(created_at_new);
  }, [appraisaDate]);

  // Handle copying text to the clipboard
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTooltipText('Copied!');
      setTooltipId(id);
      setTimeout(() => {
        setCopiedId(null);
        setTooltipId(null);
      }, 1500);
    }).catch((err) => {
      console.error('Failed to copy text:', err);
    });
  };

  if (!results || results.length === 0) return null;

  return (
    <div className="appraisal-results">
      <h3>Appraisal Results</h3>
        {((comment && comment.trim().length > 0) && (corpId && corpId > 0)) && (
          <div className="flex items-center gap-3 bg-zinc-800 pt-2">
            {corpId && (
              <img 
                src={`https://images.evetech.net/corporations/${corpId}/logo`} 
                alt="Corp Logo"
                className="ml-4 w-16 h-16 rounded" 
                loading="lazy"
              />
            )}
            <h5 className="text-lg font-mono text-neutral-200">{comment}</h5>
          </div>
        )}
      {/* Display invalid items section if any exist */}

      {invalidItems.length > 0 && (
        <div className="bg-neutral-900 border border-red-400 text-red-700 px-4 py-3 rounded relative ">
          <strong className="font-bold">Unknown items:</strong>
          <ul className="mt-2">
            {invalidItems.map((item, index) => (
              <li key={index} className="mb-2">
                <span className="font-medium">{item.name}</span>
                {item.possibleMatches?.length > 0 && (
                  <div className="text-sm mt-1">
                    Suggested items:
                    <ul className="list-disc ml-6">
                      {item.possibleMatches.map((match, idx) => (
                        <li key={idx}>{match}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="grand-totals">
          <div 
            className="total-item" 
            data-tooltip-id="sell-total-tooltip" 
            data-tooltip-content={tooltipId === 'sell-total' ? tooltipText : ''} 
            onClick={() => handleCopy(Math.trunc(sellTotal), 'sell-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Total Sell Value</strong>
            {formatNumber(sellTotal)} ISK
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="split-total-tooltip" 
            data-tooltip-content={tooltipId === 'split-total' ? tooltipText : ''} 
            onClick={() => handleCopy(Math.trunc(priceSplit), 'split-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Split Value</strong>
            {formatNumber(priceSplit)} ISK
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="buy-total-tooltip" 
            data-tooltip-content={tooltipId === 'buy-total' ? tooltipText : ''} 
            onClick={() => handleCopy(Math.trunc(buyTotal), 'buy-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Total Buy Value</strong>
            {formatNumber(buyTotal)} ISK
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="volume-total-tooltip" 
            data-tooltip-content={tooltipId === 'volume-total' ? tooltipText : ''} 
            onClick={() => handleCopy(`${formatNumber(volTotal)} m³`, 'volume-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Total Volume</strong>
            {formatNumber(volTotal)} m<sup>3</sup>
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="date-tooltip" 
            data-tooltip-content={tooltipId === 'date' ? tooltipText : ''} 
            onClick={() => handleCopy(formattedDate, 'date')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Created at</strong>
            {formattedDate}
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="market-tooltip" 
            data-tooltip-content={tooltipId === 'market' ? tooltipText : ''} 
            onClick={() => handleCopy(marketHub.toUpperCase(), 'market')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Market</strong>
            {marketHub.toUpperCase()}
          </div>
        </div>
        
        <Tooltip id="sell-total-tooltip" place="top" effect="solid" />
        <Tooltip id="split-total-tooltip" place="top" effect="solid" />
        <Tooltip id="buy-total-tooltip" place="top" effect="solid" />
        <Tooltip id="volume-total-tooltip" place="top" effect="solid" />
        <Tooltip id="date-tooltip" place="top" effect="solid" />
        <Tooltip id="market-tooltip" place="top" effect="solid" />
      </div>

<table className="results-table">
  <thead>
    <tr className="bg-neutral-800 border-b border-neutral-700">
      <th className="p-3">              
        <button
          className="p-2 w-10 h-10 rounded-lg font-medium bg-neutral-900 hover:bg-orange-400 transition-colors flex items-center justify-center gap-2 text-orange-300 hover:text-neutral-900"
          onClick={() => handleCopy(rawInput, 'raw-input')}
          data-tooltip-id="raw-input-tooltip"
          data-tooltip-content={tooltipId === 'raw-input' ? tooltipText : 'Copy raw input'}
        >
          {copiedId === 'raw-input' ? (
            <Check size={20} />
          ) : (
            <Copy size={20} />
          )}
        </button>
      </th>


      <th className="p-3 text-left">Name</th>
      <th className="p-3 text-right">Quantity</th>
      <th>
        <div className="flex flex-col gap-1">
          <button 
            onClick={() => handleSort('singleVolume')}
            className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-neutral-700 w-full transition-colors"
          >
            <span className="sort-txt">Single Volume</span>
            <span className="flex items-center">
              {sortConfig.key === 'singleVolume' ? (
                sortConfig.direction === 'asc' ? (
                  <ArrowUp size={16} className="text-orange-400 ml-2" />
                ) : (
                  <ArrowDown size={16} className="text-orange-400 ml-2" />
                )
              ) : (
                <ArrowUpDown size={16} className="text-neutral-500 ml-2" />
              )}
            </span>
          </button>
          <div className="border-t border-orange-700 mx-1"></div>
          <button 
            onClick={() => handleSort('totalVolume')}
            className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-neutral-700 w-full transition-colors"
          >
            <span className="sort-txt">Total Volume</span>
            <span className="flex items-center">
              {sortConfig.key === 'totalVolume' ? (
                sortConfig.direction === 'asc' ? (
                  <ArrowUp size={16} className="text-orange-400 ml-2" />
                ) : (
                  <ArrowDown size={16} className="text-orange-400 ml-2" />
                )
              ) : (
                <ArrowUpDown size={16} className="text-neutral-500 ml-2" />
              )}
            </span>
          </button>
        </div>
      </th>
      <th>
        <div className="flex flex-col gap-1">
          <button 
            onClick={() => handleSort('singleSell')}
            className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-neutral-700 w-full transition-colors"
          >
            <span className="sort-txt">Single Sell</span>
            <span className="flex items-center">
              {sortConfig.key === 'singleSell' ? (
                sortConfig.direction === 'asc' ? (
                  <ArrowUp size={16} className="text-orange-400 ml-2" />
                ) : (
                  <ArrowDown size={16} className="text-orange-400 ml-2" />
                )
              ) : (
                <ArrowUpDown size={16} className="text-neutral-500 ml-2" />
              )}
            </span>
          </button>
          <div className="border-t border-orange-700 mx-1"></div>
          <button 
            onClick={() => handleSort('singleBuy')}
            className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-neutral-700 w-full transition-colors"
          >
            <span className="sort-txt">Single Buy</span>
            <span className="flex items-center">
              {sortConfig.key === 'singleBuy' ? (
                sortConfig.direction === 'asc' ? (
                  <ArrowUp size={16} className="text-orange-400 ml-2" />
                ) : (
                  <ArrowDown size={16} className="text-orange-400 ml-2" />
                )
              ) : (
                <ArrowUpDown size={16} className="text-neutral-500 ml-2" />
              )}
            </span>
          </button>
        </div>
      </th>
      <th>
        <div className="flex flex-col gap-1">
          <button 
            onClick={() => handleSort('totalSell')}
            className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-neutral-700 w-full transition-colors"
          >
            <span className="sort-txt">Total Sell</span>
            <span className="flex items-center">
              {sortConfig.key === 'totalSell' ? (
                sortConfig.direction === 'asc' ? (
                  <ArrowUp size={16} className="text-orange-400 ml-2" />
                ) : (
                  <ArrowDown size={16} className="text-orange-400 ml-2" />
                )
              ) : (
                <ArrowUpDown size={16} className="text-neutral-500 ml-2" />
              )}
            </span>
          </button>
          <div className="border-t border-orange-700 mx-1"></div>
          <button 
            onClick={() => handleSort('totalBuy')}
            className="flex items-center justify-between px-3 py-1.5 rounded hover:bg-neutral-700 w-full transition-colors"
          >
            <span className="sort-txt">Total Buy</span>
            <span className="flex items-center">
              {sortConfig.key === 'totalBuy' ? (
                sortConfig.direction === 'asc' ? (
                  <ArrowUp size={16} className="text-orange-400 ml-2" />
                ) : (
                  <ArrowDown size={16} className="text-orange-400 ml-2" />
                )
              ) : (
                <ArrowUpDown size={16} className="text-neutral-500 ml-2" />
              )}
            </span>
          </button>
        </div>
      </th>
    </tr>
  </thead>

        <tbody>
          {sortedResults.map((result, index) => (
            <tr key={`${result.item_id}-${index}`}>
              <td>
                {result.item_id && (
                  <ItemImage itemId={result.item_id} name={result.name} />
                )}
              </td>
              <td className="lol group" style={{ position: 'relative', paddingRight: '60px' }}>
                <span className="truncate hover:text-orange-400 transition-colors"
                  data-tooltip-id={`tooltip-${index}`}
                  data-tooltip-content={tooltipId === `name-${index}` ? tooltipText : ''} 
                  onClick={() => handleCopy(result.name || '-', `name-${index}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {result.name || '-'}
                </span>
                {result.item_id && (
                  <div className="flex opacity-0 group-hover:opacity-100 transition-opacity gap-1 absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-zinc-800">
                      <a 
                        href={`https://everef.net/types/${result.item_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-orange-400 transition-colors p-1"
                        data-tooltip-id={`info-tooltip-${result.item_id}`}
                      >
                        <Info size={18} className="text-blue-400" />
                      </a>
                      <a 
                        href={`https://www.adam4eve.eu/commodity.php?typeID=${result.item_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-orange-400 transition-colors p-1"
                        data-tooltip-id={`chart-tooltip-${result.item_id}`}
                      >
                        <BarChart2 size={18} className="text-green-400" />
                      </a>
                      <Tooltip id={`info-tooltip-${result.item_id}`} place="top" effect="solid">
                        View item details on EVERef
                      </Tooltip>
                      <Tooltip id={`chart-tooltip-${result.item_id}`} place="top" effect="solid">
                        View market history on Adam4EVE
                      </Tooltip>
                    </div>
                )}
              </td>
              <td 
                data-tooltip-id={`tooltip-${index}`}
                data-tooltip-content={tooltipId === `quantity-${index}` ? tooltipText : ''} 
                onClick={() => handleCopy(result.quantity.toString(), `quantity-${index}`)}
                style={{ cursor: 'pointer'}}
              >
                {formatNumber(result.quantity) || '-'}
              </td>
              <td>
                {result.total_item_vol ? `${formatNumber(result.total_item_vol/result.quantity)} m³` : '-'}
                <br />
                {result.total_item_vol ? `${formatNumber(result.total_item_vol)} m³` : '-'}
              </td>
              <td>
                <div className="flex items-center gap-1">
                  {result.sell_price !== null ? (
                    <>
                      {formatNumber(result.sell_price)}
                      {result.sell_average > 0 && (() => {
                        const { percent, color } = getPriceDiffInfo(result.sell_price, result.sell_average);
                        return (
                          <>
                            <span
                              className={`ml-1 flex items-center ${color}`}
                              data-tooltip-id={`sell-tooltip-${result.item_id}`}
                            >
                              {Math.abs(percent) <= 2 ? (
                                <ArrowLeftRight size={14} />
                              ) : percent > 0 ? (
                                <ArrowUp size={14} />
                              ) : (
                                <ArrowDown size={14} />
                              )}
                            </span>
                            <Tooltip id={`sell-tooltip-${result.item_id}`} place="right" effect="solid">
                              <div>30-day average: {formatAverage(result.sell_average)} ISK</div>
                              <div className={color}>{Math.abs(percent).toFixed(1)}% {percent > 0 ? 'above' : 'below'} average</div>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </>
                  ) : '-'}
                </div>
                <div className="flex items-center gap-1">
                  {result.buy_price !== null ? (
                    <>
                      {formatNumber(result.buy_price)}
                      {result.buy_average > 0 && (() => {
                        const { percent, color } = getPriceDiffInfo(result.buy_price, result.buy_average);
                        return (
                          <>
                            <span
                              className={`ml-1 flex items-center ${color}`}
                              data-tooltip-id={`buy-tooltip-${result.item_id}`}
                            >
                              {Math.abs(percent) <= 2 ? (
                                <ArrowLeftRight size={14} />
                              ) : percent > 0 ? (
                                <ArrowUp size={14} />
                              ) : (
                                <ArrowDown size={14} />
                              )}
                            </span>
                            <Tooltip id={`buy-tooltip-${result.item_id}`} place="right" effect="solid">
                              <div>30-day average: {formatAverage(result.buy_average)} ISK</div>
                              <div className={color}>{Math.abs(percent).toFixed(1)}% {percent > 0 ? 'above' : 'below'} average</div>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </>
                  ) : '-'}
                </div>
              </td>
              <td>
                {result.total_sell_price !== null ? formatNumber(result.total_sell_price) : '-'}
                <br />
                {result.total_buy_price !== null ? formatNumber(result.total_buy_price) : '-'}
              </td>
              <Tooltip id={`tooltip-${index}`} place="top" effect="solid" />
            </tr>
          ))}
        </tbody>
      </table>
      <Tooltip id="raw-input-tooltip" place="right" effect="solid" />
    </div>
  );
};

export default AppraisalResults;
